import { FC, useState, useMemo } from "react";
import { Select, Checkbox } from "antd";
import { Rocket } from "lucide-react";
import { useGetProjects } from "~/services/project";
import { useMissionControlStore } from "../../mission-control.store";

type Props = {};

export const ProjectSelector: FC<Props> = () => {
  const { selectedProjectId, setSelectedProject } = useMissionControlStore();
  const { data: projectsData, isLoading } = useGetProjects();
  const [showArchived, setShowArchived] = useState(false);

  // Filter projects based on showArchived toggle
  const filteredProjects = useMemo(() => {
    if (!projectsData?.items) return [];
    if (showArchived) return projectsData.items;
    return projectsData.items.filter(p => p.status !== 'INACTIVE');
  }, [projectsData?.items, showArchived]);

  const handleProjectChange = (projectId: string) => {
    const project = projectsData?.items?.find((p) => p.id === projectId);
    setSelectedProject(projectId, project);
  };

  return (
    <div className="bg-white border rounded-lg p-6 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <Rocket className="text-blue-600" size={24} />
        <h2 className="text-lg font-semibold">Select Project</h2>
      </div>

      <Select
        showSearch
        size="large"
        placeholder="Select a project to begin mission control..."
        optionFilterProp="children"
        loading={isLoading}
        value={selectedProjectId}
        onChange={handleProjectChange}
        className="w-full"
        filterOption={(input, option) =>
          (option?.label ?? "")
            .toLowerCase()
            .includes(input.toLowerCase())
        }
        options={filteredProjects?.map((project) => ({
          value: project.id,
          label: `${project.name}${project.projectRef ? ` (${project.projectRef})` : ""}${project.status === 'INACTIVE' ? ' (Archived)' : ''}`,
        }))}
      />

      <div className="mt-3">
        <Checkbox
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
        >
          Show archived projects
        </Checkbox>
      </div>

      {selectedProjectId && (
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <span className="font-semibold">Active Project:</span>{" "}
            {projectsData?.items?.find((p) => p.id === selectedProjectId)?.name}
          </p>
        </div>
      )}
    </div>
  );
};
